/** URL / email patterns for plain text inside comment HTML (not inside existing anchors). */
const PLAIN_URL_REGEX =
  /(?:https?:\/\/|www\.)[^\s<>"']+|(?:[a-zA-Z0-9][-a-zA-Z0-9]*\.)+[a-zA-Z]{2,}(?:\/[^\s<>"']*)?/gi;
const PLAIN_EMAIL_REGEX =
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;

const SKIP_LINKIFY_PARENT_TAGS = new Set([
  "A",
  "SCRIPT",
  "STYLE",
  "TEXTAREA",
  "CODE",
  "PRE",
]);

export function normalizeCommentLinkHref(raw: string): string | null {
  const href = String(raw ?? "").trim();
  if (!href || /^javascript:/i.test(href)) {
    return null;
  }
  if (
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    /^tel:/i.test(href)
  ) {
    return href;
  }
  if (href.startsWith("#/")) {
    return href;
  }
  if (href.startsWith("/") && !href.startsWith("//")) {
    return href;
  }
  if (href.startsWith("#")) {
    return href;
  }
  if (href.startsWith("www.")) {
    return `https://${href}`;
  }
  if (/^[\w-]+(\.[\w-]+)+/.test(href)) {
    return `https://${href}`;
  }
  try {
    return new URL(href, window.location.origin).href;
  } catch {
    return `https://${href}`;
  }
}

/** Opens a comment body link (timeline or editor) with the same rules as the TipTap link extension. */
export function openCommentHtmlLink(
  raw: string,
  target: string = "_blank",
): void {
  const href = normalizeCommentLinkHref(raw);
  if (!href) {
    return;
  }

  if (
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    /^tel:/i.test(href)
  ) {
    window.open(href, target, "noopener,noreferrer");
    return;
  }
  if (href.startsWith("#/")) {
    window.location.hash = href;
    return;
  }
  if (href.startsWith("/") && !href.startsWith("//")) {
    window.location.hash = `#${href}`;
    return;
  }
  if (href.startsWith("#") && !href.startsWith("#/")) {
    const id = decodeURIComponent(href.slice(1));
    const el = id ? document.getElementById(id) : null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    return;
  }

  window.open(href, target, "noopener,noreferrer");
}

function decorateExistingAnchors(root: ParentNode): void {
  root.querySelectorAll("a[href]").forEach((anchor) => {
    const el = anchor as HTMLAnchorElement;
    const raw = el.getAttribute("href") ?? "";
    const normalized = normalizeCommentLinkHref(raw);
    if (!normalized) {
      return;
    }
    el.setAttribute("href", normalized);
    el.setAttribute("target", "_blank");
    el.setAttribute("rel", "noopener noreferrer");
    el.classList.add("encommenttext", "comment-body-link");
  });
}

function linkifyTextNode(textNode: Text): void {
  const parent = textNode.parentElement;
  if (!parent || SKIP_LINKIFY_PARENT_TAGS.has(parent.tagName)) {
    return;
  }
  if (parent.closest("a")) {
    return;
  }

  const text = textNode.textContent ?? "";
  if (!text.trim()) {
    return;
  }

  const combined = new RegExp(
    `${PLAIN_EMAIL_REGEX.source}|${PLAIN_URL_REGEX.source}`,
    "gi",
  );
  if (!combined.test(text)) {
    return;
  }
  combined.lastIndex = 0;

  const fragment = document.createDocumentFragment();
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = combined.exec(text)) !== null) {
    const token = match[0];
    const start = match.index;
    if (start > lastIndex) {
      fragment.appendChild(
        document.createTextNode(text.slice(lastIndex, start)),
      );
    }

    const a = document.createElement("a");
    a.className = "encommenttext comment-body-link";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = token;

    if (token.includes("@") && !token.includes("://")) {
      a.href = `mailto:${token}`;
    } else {
      const normalized = normalizeCommentLinkHref(token);
      a.href = normalized ?? token;
    }

    fragment.appendChild(a);
    lastIndex = start + token.length;
  }

  if (lastIndex < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  if (fragment.childNodes.length === 0) {
    return;
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
}

function linkifyPlainTextUrls(root: ParentNode): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node as Text);
    node = walker.nextNode();
  }
  for (const textNode of textNodes) {
    linkifyTextNode(textNode);
  }
}

function prepareInlineImages(root: ParentNode): void {
  root.querySelectorAll("img").forEach((img) => {
    img.style.cursor = "pointer";
  });
}

/** Strip inline colors on blockquotes so theme CSS controls quote text in both themes. */
function normalizeBlockquoteStyles(root: ParentNode): void {
  root.querySelectorAll("blockquote").forEach((bq) => {
    bq.removeAttribute("style");
    bq.querySelectorAll("[style]").forEach((el) => {
      const cleaned = (el.getAttribute("style") ?? "")
        .split(";")
        .map((part) => part.trim())
        .filter((part) => part && !/^color\s*:/i.test(part))
        .join("; ");
      if (cleaned) {
        el.setAttribute("style", cleaned);
      } else {
        el.removeAttribute("style");
      }
    });
  });
}

/**
 * Normalizes anchors, linkifies bare URLs/emails, and prepares images for timeline display.
 */
export function prepareCommentHtmlForDisplay(html: string): string {
  if (!html?.trim()) {
    return html;
  }

  const host = document.createElement("div");
  host.innerHTML = html;

  decorateExistingAnchors(host);
  linkifyPlainTextUrls(host);
  prepareInlineImages(host);
  normalizeBlockquoteStyles(host);

  return host.innerHTML;
}
